/**
 * 批量選取的「選取鍵 → 品牌」映射工具（去品牌化 M4 跨頁隔離守衛）。
 *
 * 背景 / 為什麼是紅線：
 * 會員頁的批量指派等級只送單一 X-Target-Brand。若把「選中品牌集合」單純
 * 用「當前頁 data.filter(選中)」推導，跨頁選取（在第 1 頁選 kshield，翻到
 * 第 2 頁再選 wudi_star）時第 1 頁那筆 row 已不在當前 data 裡 → 它的品牌漏算
 * → selectedBrands 只剩 wudi_star → isMixedBrandSelection=false → 跨品牌批量
 * 寫入的守衛被繞過，會用 wudi_star 的 X-Target-Brand 去改到 kshield 的 row。
 *
 * 修法：維護一個「選取鍵 → 品牌」的 map，選取當下（含換頁）就把 row.brand 記下；
 * 批量寫入前用這個 map 算 distinct brands，跨品牌（>1）才擋。off-page 的品牌
 * 因為記在 map 裡，翻頁後也不會消失。
 *
 * 本模組是純函數，不依賴 React / AntD，方便單元測試直接驗證跨頁情境。
 */
import type { Brand } from "../../utils/tokenManager";

/** 一筆會員 row 用來推導品牌所需的最小形狀。 */
export interface BrandBearingRow {
  id: number;
  brand: Brand;
}

/**
 * 依「這次 onChange 後仍被選中的鍵」重算「選取鍵 → 品牌」map。
 *
 * @param prev          變更前的 selectedKey→brand map（保存了 off-page 已選鍵的品牌）
 * @param selectedKeys  AntD onChange 回傳、變更後「全部」仍被選中的鍵（含跨頁）
 * @param selectedRows  AntD onChange 回傳的選中 row 物件（通常只含目前頁能解析的 row）
 * @returns             新的 map：對每個仍選中的鍵，優先用本次 selectedRows 的 brand，
 *                      否則沿用 prev 記錄的 brand；取消選取的鍵被剔除。
 *
 * 設計要點：
 * - 以 selectedKeys 為權威集合逐鍵重建（取消選取的鍵自然被丟棄，不殘留）。
 * - off-page 的鍵在 selectedRows 裡通常沒有對應 row，靠 prev 的記錄留住品牌。
 * - selectedRows 中的 undefined/null（AntD 對解析不到的鍵可能塞洞）一律略過。
 */
export function mergeSelectionBrandMap(
  prev: Map<number, Brand>,
  selectedKeys: readonly (number | string | bigint)[],
  selectedRows: readonly (BrandBearingRow | undefined | null)[],
): Map<number, Brand> {
  // 先把本次能解析到的 row 依 id 索引起來，供優先取用。
  const rowBrandById = new Map<number, Brand>();
  for (const row of selectedRows) {
    if (row && row.brand != null) {
      rowBrandById.set(row.id, row.brand);
    }
  }

  const next = new Map<number, Brand>();
  for (const key of selectedKeys) {
    const id = typeof key === "number" ? key : Number(key);
    if (!Number.isFinite(id)) continue;
    // 優先用本次選中 row 的品牌（會反映最新資料），退回沿用先前記下的品牌。
    const brand = rowBrandById.get(id) ?? prev.get(id);
    if (brand != null) {
      next.set(id, brand);
    }
    // 若本次與 prev 都查不到品牌（理論上不會發生：選中必先在某頁 render 過），
    // 這個鍵先不入 map；deriveSelectedBrands 的呼叫端另有「無法判定品牌」的擋提交防線。
  }
  return next;
}

/** 從「選取鍵 → 品牌」map 取 distinct 品牌清單（順序不保證）。 */
export function deriveSelectedBrands(map: Map<number, Brand>): Brand[] {
  return Array.from(new Set(map.values()));
}

/** 一筆 tier 用來依品牌過濾所需的最小形狀。 */
export interface BrandBearingTier {
  id: number;
  name: string;
  brand: Brand;
}

/**
 * 依目標品牌過濾 tier 清單（VPN-99）。
 *
 * 背景：`GET /api/admin/tiers` 讀軸改用 resolveAdminViewBrand 後，聚合檢視
 * （無 X-Brand）會回「全品牌」tiers。會員頁的行內指派 / 批量指派 tier 下拉
 * 直接用這份全品牌清單時，operator 可能選到別品牌的 tier → 送出後端
 * assertTierInBrand 查無 → 回「方案不存在」。此純函數依「該操作的目標品牌」
 * （行內=該 row 品牌、批量=選取集合唯一品牌）過濾出可用 tier。
 *
 * @param tiers  全品牌（或任意品牌）tier 清單
 * @param brand  目標品牌；null/undefined（無法判定，如跨品牌批量）→ 回空陣列
 *               （寧可下拉空、不讓 operator 選到會炸的 tier）
 * @returns      只含 `brand` 相符的 tier；順序保留輸入順序
 */
export function filterTiersByBrand<T extends BrandBearingTier>(
  tiers: readonly T[],
  brand: Brand | null | undefined,
): T[] {
  if (brand == null) return [];
  return tiers.filter((t) => t.brand === brand);
}
